import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStores, createStore, updateStore, deleteStore, getActiveChains, downloadStoresTemplate, uploadStoresFile } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Pencil, Trash2, Search, Settings, Download, Upload, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const PHOTO_TYPES = ['gondola', 'precio', 'promo', 'bodega'];

export default function StoresPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [stores, setStores] = useState([]);
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterChain, setFilterChain] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    chain_id: '',
    address: '',
    lat: '',
    lng: '',
    geofence_radius_m: 100,
    min_visit_minutes: 30,
    max_visit_minutes: 120,
    required_photo_types: ['gondola', 'precio'],
    photo_capture_window_seconds: 300,
    reincidence_window_days: 30,
    reincidence_threshold: 2
  });

  useEffect(() => {
    loadData();
  }, [search, filterChain]);

  const loadData = async () => {
    try {
      const [storesRes, chainsRes] = await Promise.all([
        getStores({ 
          search: search || undefined,
          chain_id: filterChain !== 'all' ? filterChain : undefined
        }),
        getActiveChains()
      ]);
      setStores(storesRes.data);
      setChains(chainsRes.data);
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.chain_id) {
      toast.error('Debes seleccionar una cadena');
      return;
    }
    
    if (!formData.code) {
      toast.error('Debes ingresar un código de tienda');
      return;
    }
    
    try {
      const data = {
        ...formData,
        code: formData.code.toUpperCase(),
        lat: parseFloat(formData.lat),
        lng: parseFloat(formData.lng),
        geofence_radius_m: parseInt(formData.geofence_radius_m),
        min_visit_minutes: parseInt(formData.min_visit_minutes),
        max_visit_minutes: parseInt(formData.max_visit_minutes),
        photo_capture_window_seconds: parseInt(formData.photo_capture_window_seconds),
        reincidence_window_days: parseInt(formData.reincidence_window_days),
        reincidence_threshold: parseInt(formData.reincidence_threshold)
      };

      if (editing) {
        await updateStore(editing.id, data);
        toast.success('Tienda actualizada');
      } else {
        await createStore(data);
        toast.success('Tienda creada');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando tienda');
    }
  };

  const handleEdit = (store) => {
    setEditing(store);
    setFormData({
      code: store.code || '',
      name: store.name,
      chain_id: store.chain_id,
      address: store.address,
      lat: store.lat,
      lng: store.lng,
      geofence_radius_m: store.geofence_radius_m,
      min_visit_minutes: store.min_visit_minutes,
      max_visit_minutes: store.max_visit_minutes,
      required_photo_types: store.required_photo_types || [],
      photo_capture_window_seconds: store.photo_capture_window_seconds,
      reincidence_window_days: store.reincidence_window_days,
      reincidence_threshold: store.reincidence_threshold
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar esta tienda?')) return;
    try {
      await deleteStore(id);
      toast.success('Tienda desactivada');
      loadData();
    } catch (error) {
      toast.error('Error desactivando tienda');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      code: '',
      name: '',
      chain_id: '',
      address: '',
      lat: '',
      lng: '',
      geofence_radius_m: 100,
      min_visit_minutes: 30,
      max_visit_minutes: 120,
      required_photo_types: ['gondola', 'precio'],
      photo_capture_window_seconds: 300,
      reincidence_window_days: 30,
      reincidence_threshold: 2
    });
  };

  const togglePhotoType = (type) => {
    setFormData(prev => ({
      ...prev,
      required_photo_types: prev.required_photo_types.includes(type)
        ? prev.required_photo_types.filter(t => t !== type)
        : [...prev.required_photo_types, type]
    }));
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadStoresTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_tiendas.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error descargando plantilla');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await uploadStoresFile(file);
      const { created, updated, errors } = response.data;
      
      if (errors?.length > 0) {
        toast.warning(`Creados: ${created}, Actualizados: ${updated}, Errores: ${errors.length}`);
      } else {
        toast.success(`Importación exitosa. Creados: ${created}, Actualizados: ${updated}`);
      }
      loadData();
    } catch (error) {
      toast.error('Error importando archivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="stores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Tiendas</h1>
          <p className="text-slate-500">Gestiona las tiendas y sus configuraciones</p>
        </div>
        <div className="flex gap-2">
          {/* Excel buttons */}
          <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-2" /> Plantilla Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-excel-btn"
          >
            <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Importando...' : 'Importar Excel'}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx,.xls"
            className="hidden"
          />
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-store-btn">
                <Plus className="w-4 h-4 mr-2" /> Nueva Tienda
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? 'Editar Tienda' : 'Nueva Tienda'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Código *</Label>
                    <Input
                      value={formData.code}
                      onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                      placeholder="WAL-001"
                      required
                      data-testid="store-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cadena *</Label>
                    <Select value={formData.chain_id} onValueChange={(v) => setFormData({...formData, chain_id: v})}>
                      <SelectTrigger data-testid="store-chain-select">
                        <SelectValue placeholder="Seleccionar cadena" />
                      </SelectTrigger>
                      <SelectContent>
                        {chains.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre de Sucursal *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Sucursal Centro"
                      required
                      data-testid="store-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Dirección</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    required
                    data-testid="store-address-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Latitud</Label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.lat}
                      onChange={(e) => setFormData({...formData, lat: e.target.value})}
                      required
                      data-testid="store-lat-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Longitud</Label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.lng}
                      onChange={(e) => setFormData({...formData, lng: e.target.value})}
                      required
                      data-testid="store-lng-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Radio Geocerca (m)</Label>
                    <Input
                      type="number"
                      value={formData.geofence_radius_m}
                      onChange={(e) => setFormData({...formData, geofence_radius_m: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tiempo Mínimo (min)</Label>
                    <Input
                      type="number"
                      value={formData.min_visit_minutes}
                      onChange={(e) => setFormData({...formData, min_visit_minutes: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tiempo Máximo (min)</Label>
                    <Input
                      type="number"
                      value={formData.max_visit_minutes}
                      onChange={(e) => setFormData({...formData, max_visit_minutes: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Fotos Requeridas</Label>
                  <div className="flex flex-wrap gap-2">
                    {PHOTO_TYPES.map(type => (
                      <Badge
                        key={type}
                        variant={formData.required_photo_types.includes(type) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => togglePhotoType(type)}
                      >
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Ventana Captura (seg)</Label>
                    <Input
                      type="number"
                      value={formData.photo_capture_window_seconds}
                      onChange={(e) => setFormData({...formData, photo_capture_window_seconds: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ventana Reincidencia (días)</Label>
                    <Input
                      type="number"
                      value={formData.reincidence_window_days}
                      onChange={(e) => setFormData({...formData, reincidence_window_days: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Umbral Reincidencia</Label>
                    <Input
                      type="number"
                      value={formData.reincidence_threshold}
                      onChange={(e) => setFormData({...formData, reincidence_threshold: e.target.value})}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="store-submit-btn">
                    {editing ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar tiendas..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-stores-input"
              />
            </div>
            <Select value={filterChain} onValueChange={setFilterChain}>
              <SelectTrigger data-testid="filter-chain">
                <SelectValue placeholder="Filtrar por cadena" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las cadenas</SelectItem>
                {chains.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={`stores-skeleton-${i}`} className="h-12" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Código</TableHead>
                  <TableHead>Cadena</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Dirección</TableHead>
                  <TableHead>Geocerca</TableHead>
                  <TableHead>Secciones</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stores.map((store) => (
                  <TableRow key={store.id} data-testid={`store-row-${store.id}`}>
                    <TableCell>
                      <span className="font-mono text-sm font-medium text-blue-600">{store.code || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-medium">
                        {store.chain_code || store.chain_name || '-'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{store.name}</TableCell>
                    <TableCell className="text-slate-500 max-w-xs truncate">{store.address}</TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">{store.geofence_radius_m}m</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{store.sections_count || 0}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={store.active ? 'default' : 'secondary'}>
                        {store.active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => navigate(`/stores/${store.id}/config`)}
                        title="Configurar secciones y SKUs"
                        data-testid={`config-store-${store.id}`}
                      >
                        <Settings className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(store)} data-testid={`edit-store-${store.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(store.id)} data-testid={`delete-store-${store.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {stores.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      {chains.length === 0 
                        ? 'Primero crea una cadena de supermercados'
                        : 'No hay tiendas registradas'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
